import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 組織体制ページ 全面作り直し（Implementer C スコープ） =====
// デザイン土台: src/pages/about/overview/page.tsx（型の正典）と src/pages/home/page.tsx。
//   紺#1a2942 / 真鍮金#a38e65 / 金差し色見出し / リード3行+ / BTN-08 / AboutSideNav 配線。
//   charter §3: 組織図・部門一覧をエディトリアルに。役職名・部門名は22px級serifで強調・細罫整列・カード乱立禁止。
// 事実正典（創作禁止・出典path記録／外部実績主張の復活は禁止）:
//   [S1] mng-thebutler/00_事業説明.md L35（代々の継承＝三代目 石井裕一）, L39（お台場・執事サロン）
//   [S2] 現行 about/organization/page.tsx 実文（役員2名・6部門・特徴4・各部門活動5＝内部完結表現。海外提携等の主張は復活させない）
//   [S3] overview corporateFacts（代表理事 石井 裕一〈三代目頭取〉／1890年創立）
// ここに無い年号・数値・役職は書かない。

export default function OrganizationPage() {
  useEffect(() => {
    updateSEO(seoData['/about/organization']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '協会について', href: '/about' },
    { label: '組織体制' }
  ];

  const aboutNav = [
    { label: '協会概要', href: '/about/overview' },
    { label: '理念と使命', href: '/about/mission' },
    { label: '沿革・歴史', href: '/about/history' },
    { label: '組織体制', href: '/about/organization' },
    { label: '主な活動', href: '/about/activities' },
    { label: 'アクセス・所在地', href: '/about/access' }
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // [S2][S3] 役員
  const officers = [
    {
      role: '代表理事', roman: 'I.', name: '石井 裕一', sub: '三代目頭取',
      desc: '協会全体の運営方針を決定し、執事文化の発展を統括します。対外的な代表として関係機関との連携を推進し、執事という職業の社会的価値の向上に取り組んでいます。',
    },
    {
      role: '副理事長', roman: 'II.', name: '経営顧問', sub: 'Advisor',
      desc: '代表理事を補佐し、協会運営の基盤を支えます。長年の経験を活かした助言・指導により、各種事業の調整および監督を行い、組織の安定した運営に貢献しています。',
    },
  ];

  // [S2] 部門構成（6部門・部門名は22px serif強調・細罫整列）
  const departments = [
    { no: '01', name: '教育研修部門', tags: 'カリキュラム開発 ／ 講師育成', desc: '執事の専門知識と技術の体系化、研修プログラムの開発・実施を担当します。研修内容の継続的な改善も推進しています。' },
    { no: '02', name: '認定・資格部門', tags: '資格審査 ／ 継続教育', desc: '執事資格の認定基準の策定と審査業務を担当します。継続教育制度の運営と品質管理を通じて、資格の信頼性を守ります。' },
    { no: '03', name: '国際交流部門', tags: '国際対応 ／ 語学研修', desc: '国際的に通用する執事の育成、海外からのお客様への対応力の研究、海外の執事文化の調査を担います。' },
    { no: '04', name: '広報・情報部門', tags: '広報活動 ／ 情報発信', desc: '協会活動の広報、ウェブサイトの運営、メディア対応を担当し、執事文化の普及と認知度の向上に努めます。' },
    { no: '05', name: '運営管理部門', tags: '総務・財務 ／ 会員管理', desc: '日常業務の運営、会員管理、財務管理、総務業務を担い、各部門の活動を支える基盤組織として機能します。' },
    { no: '06', name: 'アドバイザリーボード', tags: '専門助言 ／ 戦略支援', desc: '各分野の専門家による助言機関です。経験と知見を活かし、協会運営に戦略的な助言を提供します。' },
  ];

  // [S2] 組織構造図（下位6部門）
  const chartUnits = ['教育研修部門', '認定・資格部門', '国際交流部門', '広報・情報部門', '運営管理部門', 'アドバイザリーボード'];

  // [S2] 各部門の主な活動（5詳細）
  const activities = [
    { name: '教育研修部門', desc: '執事の専門知識を体系化し、段階的な学習カリキュラムを開発しています。Associate／Professional／Master Butler の各レベルに応じた教材作成、実技研修プログラムの企画、講師陣の育成を担当。食事・旅行・ビジネスマナーなどの専門分野別カリキュラムも整備し、受講者の多様なキャリアパスを支えます。' },
    { name: '認定・資格部門', desc: '執事資格の認定基準の策定と定期的な見直しを行い、一貫した水準の資格制度を運営しています。試験問題の作成、採点基準の設定、実技試験の実施管理など、公正かつ厳格な審査体制を維持。資格取得後の継続教育プログラムも管理し、資格保持者が常に最新の知識と技能を保てるよう支援します。' },
    { name: '国際交流部門', desc: '海外の執事文化やサービスの動向を調査・研究し、国際的に通用する執事の育成に活かしています。国際儀礼や異文化理解を扱うカリキュラムの開発、海外からのお客様への対応力の強化、外国人受講者の受け入れ体制の整備など、グローバルな視点での執事教育を進めています。' },
    { name: '広報・情報部門', desc: '協会の活動を広く社会に発信し、執事文化の認知度向上に努めています。ウェブサイトの運営管理、SNS での情報発信、メディア対応、プレスリリースの作成など、多様なチャネルを通じた広報を展開。資格取得者の活躍事例の紹介や、執事文化の普及イベントの企画も担います。' },
    { name: '運営管理部門', desc: '協会の円滑な運営を支える基盤業務を担当しています。会員登録の管理、受講者データベースの整備、財務管理、法務対応、総務業務など、組織運営に必要な管理業務を統括。各部門の活動を横断的に支え、効率的な業務フローの構築や設備・備品の管理を通じて協会全体の運営基盤を強化します。' },
  ];

  // [S2] 組織運営の特徴（4）
  const features = [
    { title: '専門性重視', desc: '各分野の専門家による委員会制度を採用し、実務経験の豊富な専門家が協会運営に参画しています。業界の最新の動向を反映した、質の高いサービスの提供につなげています。' },
    { title: '国際対応', desc: '国際的な視野を重視し、海外からのお客様にも対応できる執事の育成に取り組んでいます。国際的な水準を研究し、教育プログラムの開発に活かしています。' },
    { title: '継続的改善', desc: '定期的な組織評価と改善活動を実施し、時代の変化に対応した柔軟な組織運営を心がけています。会員のニーズに応える、持続可能な発展を目指します。' },
    { title: '透明性確保', desc: '組織運営の透明性を重視し、定期的な活動報告を行っています。会員や社会に対する説明責任を果たし、信頼される組織運営を実践しています。' },
  ];

  return (
    <Layout>

      {/* ===== ヒーロー — 全幅ビルボード。由来: overview ヒーロー／カタログ BB-02（左寄せ打ち出し型） ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/related/butler-bell/bell-gate-wide.png"
          alt="迎賓門に立つ代表理事・石井裕一（三代目頭取）の本人実写"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Organization</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">専門性で支える、</span>
              <span className="block">協会の組織体制</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              全日本執事協会は、各専門分野の責任者が連携し、執事文化の発展と質の高い教育プログラムの提供に取り組んでいます。役員体制のもとで六つの部門が機能し、会員と受講者の皆様へ最高水準のサービスをお届けします。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== 本文 = サイドナビ + コンテンツの2カラム ===== */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            {/* サイドナビ（共通コンポーネント・organization を強調） */}
            <AboutSideNav items={aboutNav} activeHref="/about/organization" />

            {/* コンテンツ列 */}
            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* --- I. リード + 細罫データ行。由来: overview S1 文法 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Governance</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  連携で動く、<span className="text-[#a38e65]">専門家の組織</span>
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  <p>
                    全日本執事協会の運営は、各専門分野の責任者が連携する体制によって支えられています。代表理事を頂点とした役員体制のもと、教育・研修から資格認定、国際交流、広報、運営管理までを担う六つの部門が、それぞれの専門性を発揮しています。
                  </p>
                  <p>
                    登録講師制度や専門家アドバイザー、業務委託パートナーといった外部の協力体制も活用し、少数精鋭でありながら質の高い運営を実現しています。各部門は独立して機能するのではなく、共通の理念のもとで密に連携しています。
                  </p>
                  <p>
                    こうした組織のかたちは、会員および受講者の皆様に、いつでも安定した最高水準のサービスをお届けするための基盤です。組織の力とは、個人の力の総和以上のものだと私たちは考えています。
                  </p>
                </div>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">部門</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">6<span className="text-sm text-[#8f7a56] ml-1">部門</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">役員</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">2<span className="text-sm text-[#8f7a56] ml-1">名</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">継承</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">3<span className="text-sm text-[#8f7a56] ml-1">代</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">本部</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">東京・お台場</dd>
                  </div>
                </dl>
              </section>

              {/* --- II. 理事会・役員 = 新バリエーション①
                   由来注記: overview S2（定義リストの罫線文法）を「役員名鑑」へ翻案した新造。
                   氏名を28px級の大serifで主役に据え、役職・注記・略歴を細罫で整列。右に理事会の実写。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Board of Directors</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  理事会・役員体制
                </h2>
                <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
                  <div className="border-t-2 border-[#1a2942]">
                    {officers.map((o) => (
                      <div key={o.role} className="border-b border-[#e2ddd2] py-7">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm">{o.roman}</span>
                          <span className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em]">{o.role}</span>
                        </div>
                        <div className="flex items-baseline gap-4 mb-4">
                          <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-[28px] leading-tight">{o.name}</h3>
                          <span className="font-['Playfair_Display',serif] italic text-[#a38e65] text-sm">{o.sub}</span>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#4a4638] leading-[1.9] max-w-2xl">{o.desc}</p>
                      </div>
                    ))}
                  </div>
                  <div className="lg:pt-1">
                    <img
                      src="/images/salon0926/20260912_094946.jpg"
                      alt="執事サロンの正餐の間にあるカーテン側の壁面とテーブル"
                      className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                    />
                  </div>
                </div>
              </section>

              {/* --- III. 部門構成 = 新バリエーション②
                   由来注記: カタログ SEC-N04（積層splitカード）を、カード枠を廃した「細罫の番号付き整列リスト」へ翻案した新造。
                   部門名を22px級serifで強調し、六つの部門を一覧で整然と読ませる（カード乱立を避ける）。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Departments</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  六つの部門構成
                </h2>
                <img
                  src="/images/generated/seq-board-meeting-japanese-3b89cc.jpg"
                  alt="会議室で部門を越えて協議するスタッフたち。専門性を活かした協会の部門体制"
                  className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
                />
                <div className="border-t-2 border-[#1a2942]">
                  {departments.map((d) => (
                    <div key={d.no} className="flex flex-col sm:flex-row gap-3 sm:gap-8 border-b border-[#e2ddd2] py-6">
                      <div className="sm:w-[300px] sm:shrink-0 flex items-baseline gap-4">
                        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none">{d.no}</span>
                        <div>
                          <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-[22px] leading-tight mb-1.5">{d.name}</h3>
                          <span className="block font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.04em]">{d.tags}</span>
                        </div>
                      </div>
                      <p className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{d.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- IV. 組織構造図（階層図・紺ボックス+細罫）。由来: 組織図の固有型（overview に無い構造図）。
                   代表理事→副理事長→六部門を細罫でつなぐ、装飾記号なしの整った階層図。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Structure</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  組織構造図
                </h2>
                <div className="border border-[#e2ddd2] bg-[#f8f6f0] px-6 sm:px-10 py-12 sm:py-14">
                  {/* 代表理事 */}
                  <div className="flex flex-col items-center">
                    <div className="bg-[#1a2942] text-center px-8 sm:px-12 py-5 min-w-[200px]">
                      <span className="block font-['Noto_Serif_JP',serif] text-[11px] text-[#d7b779] tracking-[0.12em] mb-1">代表理事</span>
                      <span className="jba-display font-medium text-white text-lg sm:text-xl">石井 裕一</span>
                    </div>
                    <div className="w-px h-8 bg-[#a38e65]"></div>
                    {/* 副理事長 */}
                    <div className="bg-[#a38e65] text-center px-7 sm:px-10 py-4 min-w-[180px]">
                      <span className="block font-['Noto_Serif_JP',serif] text-[11px] text-white/80 tracking-[0.12em] mb-0.5">副理事長</span>
                      <span className="jba-display font-medium text-white text-base sm:text-lg">経営顧問</span>
                    </div>
                    <div className="w-px h-8 bg-[#a38e65]"></div>
                  </div>
                  {/* 六部門 */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 border-t border-l border-[#a38e65]/30">
                    {chartUnits.map((u) => (
                      <div key={u} className="border-r border-b border-[#a38e65]/30 bg-white px-4 py-5 text-center flex items-center justify-center">
                        <span className="jba-display font-medium text-[#1a2942] text-sm sm:text-[15px] leading-snug">{u}</span>
                      </div>
                    ))}
                  </div>
                  {/* 外部連携 */}
                  <div className="mt-10 pt-8 border-t border-[#a38e65]/25">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg text-center mb-6">外部連携・協力体制</h3>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {[
                        { t: '登録講師制度', d: '専門分野の実務経験者による講師陣' },
                        { t: '専門家アドバイザー', d: '各分野の専門家による助言・指導' },
                        { t: '業務委託パートナー', d: '効率的な運営を支える外部協力機関' },
                      ].map((x) => (
                        <div key={x.t} className="border-t border-[#a38e65]/40 pt-4 text-center">
                          <h4 className="jba-display font-medium text-[#1a2942] text-base mb-1.5">{x.t}</h4>
                          <p className="font-['Noto_Serif_JP',serif] text-[13px] text-[#4a4638] leading-[1.8]">{x.d}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* --- V. 各部門の主な活動（左真鍮罫エディトリアルブロック）。
                   由来: カタログ SEC-04（引用・声＝左真鍮罫）を、部門活動の詳述ブロックへ応用。本文を厚く保持。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. What Each Team Does</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  各部門の主な活動
                </h2>
                <img
                  src="/images/gen2/jba-organization-training-education-01.jpg"
                  alt="講師と受講者が研修内容を確認する様子。教育活動に取り組む部門スタッフのイメージ"
                  className="w-full aspect-[21/9] h-auto object-cover object-top border border-[#a38e65]/30 mb-10"
                />
                <div className="space-y-6">
                  {activities.map((a) => (
                    <div key={a.name} className="border-l-2 border-[#a38e65] bg-[#f8f6f0] pl-6 sm:pl-8 pr-5 sm:pr-8 py-6">
                      <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-[22px] mb-3 leading-[1.4]">{a.name}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{a.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- VI. 組織運営の特徴（紺地・2×2グリッド）。由来: overview S3 の紺パネル文法を運営特徴へ展開。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Our Approach</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  組織運営の特徴
                </h2>
                <div className="bg-[#1a2942] px-7 sm:px-10 lg:px-14 py-12 sm:py-14">
                  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-9">
                    {features.map((f, i) => (
                      <div key={f.title} className="border-t border-[#a38e65]/40 pt-5">
                        <div className="flex items-baseline gap-3 mb-2.5">
                          <span className="font-['Playfair_Display',serif] italic text-[#d7b779] text-sm">{`0${i + 1}`}</span>
                          <h3 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl">{f.title}</h3>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#ded8cc] leading-[1.9]">{f.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* --- VII. 締めCTA帯。由来: カタログ SEC-N12（締めCTA帯）／ overview S6 紺CTAパネル --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VII. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  執事文化の発展に、ともに
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    全日本執事協会は、執事文化に関心のある方、プロフェッショナルを目指す方の参加をお待ちしています。協会の活動や資格制度について、どうぞお気軽にお問い合わせください。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/about/activities" className={BTN08_ON_NAVY}>主な活動を見る</a>
                    <a href="/qualifications" className={BTN08_ON_NAVY}>執事資格制度を見る</a>
                    <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
